// Third Party
import * as React from 'react';
import { findIndex, toInteger } from 'lodash';
import cx from 'classnames';
import 'react-dates/initialize';
import moment from 'moment';
import Handlebars from 'handlebars';
import Dayjs from 'dayjs';
import { useSession } from 'next-auth/react';

// Utils
import { useNotification } from '@contexts/use-notification';
import {
  enumSupportedCheckInAndOutTimesToString,
  enumToStringPropertyTypes,
  enumToStringUniquePropertyTypes,
} from '@utils/constants/enums';
import { useToggle } from '@hooks';
import { appendUrlParams } from '@utils/append-url-params';
import { filterSafetyFeatures, ListingBySlugPartial } from './utils';
import {
  AffiliationType,
  BookDirectWebsiteUrlParamsTemplateInput,
  Feature,
  QuoteItemizedType,
  useListingFavoriteCreateMutation,
  useListingFavoriteDeleteMutation,
} from '@gql';
import { formatWholeDollarAmount } from '@utils/money';
import { useLoginAndSignupModals } from '@contexts/use-login-and-signup-modals';
import { useQuotes } from './quotes-context';
import { useReviews } from './reviews-context';
import { formatDateMMMD } from '@utils/date-helpers';
import { routes } from '@routes';
import { useAnalytics } from '@contexts/use-analytics';

// UI
import { AnalyticsRef } from '@components/analytics-ref';
import { AssociationsSection } from './associations-section';
import { AwardsSection } from './awards-section';
import { Button } from '@components/button';
import { CancellationPolicy } from './cancellation-policy';
import { Description } from './description';
import { FavoriteButton } from '@components/favorite-button';
import { FeaturesSection } from './features-section';
import { HeadingAndTitleBar } from './heading-and-title-bar';
import { Icon, IconNames } from '@components/icon';
import { ImageMural } from './image-mural';
import { InnkeeperBio } from './innkeeper-bio';
import { InspectionsSection } from './inspections-section';
import { Link } from '@components/link';
import { ManagementBio } from './management-bio';
import { MapPreview } from './map-preview';
import { PropertyManagerBio } from './property-manager-bio';
import { QuotesCta } from './quotes-cta/quotes-cta';
import { QuotesModal } from './quotes-modal';
import { ReviewsModal } from './reviews-modal';
import { ReviewsSection } from './reviews-section';
import { SafetyFeaturesSection } from './safety-features-section';
import { ShareModal } from './heading-and-title-bar/share-modal';
import { Spinner } from '@components/spinner';
import { Typography } from '@components/typography';
import { QuotesCompare } from './quotes-compare/quotes-compare';

import Image from 'next/image';
import noServiceFees from '../../../public/assets/svg/illustrations/no_fees_slate.svg';
import professionalHosts from '../../../public/assets/svg/illustrations/pro_hosts_slate.svg';

type Props = { listing: ListingBySlugPartial };

export const ListingShow: React.FC<Props> = ({ listing }) => {
  const { data: session } = useSession();
  const { addNotification } = useNotification();
  const { activateLoginModal } = useLoginAndSignupModals();
  const {
    activateQuotesModal,
    checkInDate,
    checkOutDate,
    deactivateQuotesModal,
    datesPresent,
    bookingQuotePresent,
    quoteData,
    quoteDataLoading,
    quotesModalActive,
    adultCount,
    infantCount,
    petCount,
    childCount,
    numberOfNights,
  } = useQuotes();

  const { activateReviewsModal } = useReviews();

  const {
    on: shareModalActive,
    setOff: deactivateShareModal,
    setOn: activateShareModal,
  } = useToggle();

  const [listingFavoriteCreateMutation] = useListingFavoriteCreateMutation();
  const [listingFavoriteDeleteMutation] = useListingFavoriteDeleteMutation();

  const [fav, setFav] = React.useState(listing.isFavorited);
  const [favLoading, setFavLoading] = React.useState<boolean>(false);

  async function handleFav(listingId: string): Promise<void> {
    if (session?.user != null) {
      setFav(!fav);
      setFavLoading(true);
      try {
        if (!fav == true) {
          await listingFavoriteCreateMutation({
            variables: { input: { listingId: listingId } },
          });
        } else {
          await listingFavoriteDeleteMutation({
            variables: { input: { listingId: listingId } },
          });
        }
      } catch (errors) {
        setFav(fav);
        addNotification({
          type: 'error',
          title: 'Something went wrong...',
          message: 'Please try again later.',
        });
      } finally {
        setFavLoading(false);
      }
    } else {
      activateLoginModal();
    }
  }

  // TODO - Do we really need this?
  const shouldShowWebsiteUrlAsButton =
    (listing.marketingWebsiteUrl || listing.account.marketingWebsiteUrl) &&
    !listing.bookDirectWebsiteUrl
      ? true
      : false;

  const shouldShowBookingUrl = listing.bookDirectWebsiteUrl ? true : false;

  const shouldShowRatesAndAvailability = listing.supportsQuotes;

  const categorizedFeatures: [
    {
      label: string;
      features: Pick<
        Feature,
        'key' | 'displayName' | 'categoryName' | 'promoted'
      >[];
    },
  ] = [{ label: 'Popular Amenities', features: [] }];
  // Decorate above object with new categories.
  listing.features
    .filter((feature) => feature.categoryName !== 'Safety Measures')
    .forEach((feature) => {
      if (feature.promoted) {
        const i = findIndex(
          categorizedFeatures,
          (category) => category.label === 'Popular Amenities'
        );
        categorizedFeatures[i].features.push(feature);
      } else {
        const i = findIndex(
          categorizedFeatures,
          (cat) => cat.label === feature.categoryName
        );
        // If category exists in our object, add features.
        if (i > -1) {
          categorizedFeatures[i].features.push(feature);
        }
        // Otherwise create new category object initialized with feature.
        else {
          categorizedFeatures.push({
            label: feature.categoryName,
            features: [feature],
          });
        }
      }
    });

  const safetyFeatures = filterSafetyFeatures(listing);
  const listingAwards =
    listing?.affiliations?.filter(
      (affiliation) => affiliation.type === AffiliationType.Awards
    ) || [];
  const listingAssociations =
    listing?.affiliations?.filter(
      (affiliation) => affiliation.type === AffiliationType.Associations
    ) || [];
  const listingInspections =
    listing?.affiliations?.filter(
      (affiliation) => affiliation.type === AffiliationType.Inspections
    ) || [];

  const featuresList = categorizedFeatures
    .reduce(
      (
        previousValue: Pick<
          Feature,
          'key' | 'displayName' | 'categoryName' | 'promoted'
        >[],
        currentValue
      ) => previousValue.concat(currentValue.features),
      []
    )
    .sort((value) => {
      return value.categoryName !== 'Popular Amenities' ? 1 : -1; // `true` values first
    });
  const { on: showAllAmenities, toggle: toggleShowAllAmenities } =
    useToggle(false);

  const { on: showAllReviews, toggle: toggleShowAllReviews } = useToggle(false);

  const priceRangeText = () => {
    if (
      listing.maxPrice &&
      listing.minPrice &&
      listing.maxPrice == listing.minPrice
    ) {
      return `${formatWholeDollarAmount(listing.minPrice)}`;
    }
    if (listing.maxPrice && listing.minPrice) {
      return `${formatWholeDollarAmount(
        listing.minPrice
      )} - ${formatWholeDollarAmount(listing.maxPrice)}`;
    }
    if (listing.minPrice) {
      return `${formatWholeDollarAmount(listing.minPrice)}`;
    }

    if (listing.maxPrice) {
      return `${formatWholeDollarAmount(listing.maxPrice)}`;
    }

    return '';
  };

  const [cta, setCta] = React.useState<{
    label: string;
    subtext?: string;
    analyticsRef: string;
    bookingUrl?: string;
  }>({ label: '', subtext: '', analyticsRef: '' });

  function constructAdditionalParams(
    additionalParams: BookDirectWebsiteUrlParamsTemplateInput
  ) {
    return additionalParams;
  }

  const { setCurrentListing } = useAnalytics();

  React.useEffect(() => {
    setCurrentListing(listing);
  }, [listing]);

  React.useEffect(() => {
    let bookingUrl: string | undefined;

    if (
      listing.bookDirectWebsiteUrl &&
      listing.bookDirectWebsiteUrlParamsTemplate
    ) {
      Handlebars.registerHelper('formatDate', function (date, format) {
        return Dayjs(date).format(format);
      });
      Handlebars.registerHelper('add', function (...args) {
        const numbers = args.slice(0, -1); // last argument is options object
        return Math.round(
          numbers.reduce((sum, number) => sum + parseFloat(number), 0)
        );
      });

      const template = Handlebars.compile(
        listing.bookDirectWebsiteUrlParamsTemplate
      );

      const additionalParams = constructAdditionalParams({
        checkInDate: checkInDate || '',
        checkOutDate: checkOutDate || '',
        adultCount: adultCount,
        childCount: childCount,
        infantCount: infantCount,
        petCount: petCount,
      });

      const additionalParamsString = template(additionalParams);

      const separator = listing.bookDirectWebsiteUrl.includes('?') ? '&' : '?';
      const redirectUrl =
        listing.bookDirectWebsiteUrl + separator + additionalParamsString;

      bookingUrl = routes.deeplink(listing.slug, redirectUrl);
    } else if (listing.supportsBookings) {
      bookingUrl = routes.book({
        slug: listing.slug,
        adultCount: adultCount,
        checkInDate: checkInDate ? checkInDate : undefined,
        checkOutDate: checkOutDate ? checkOutDate : undefined,
        childCount: childCount,
        infantCount: infantCount,
        petCount: petCount,
      });
    } else if (listing.bookDirectWebsiteUrl) {
      bookingUrl = routes.deeplink(
        listing.slug,
        appendUrlParams(listing.bookDirectWebsiteUrl, {
          utm_source: 'Savvy',
          utm_medium: 'website',
        })
      );
    } else if (listing.marketingWebsiteUrl) {
      bookingUrl = routes.deeplink(
        listing.slug,
        appendUrlParams(listing.marketingWebsiteUrl, {
          utm_source: 'Savvy',
          utm_medium: 'website',
        })
      );
    } else if (listing.account.marketingWebsiteUrl) {
      bookingUrl = routes.deeplink(
        listing.slug,
        appendUrlParams(listing.account.marketingWebsiteUrl, {
          utm_source: 'Savvy',
          utm_medium: 'website',
        })
      );
    } else {
      bookingUrl = undefined;
    }

    if (quotesModalActive) {
      setCta({ label: 'Save', analyticsRef: 'save_dates' });
    } else if (
      listing.supportsQuotes &&
      (!(checkInDate && checkOutDate) ||
        (quoteData &&
          quoteData.bookingQuoteByListingAndDates.__typename ==
            'ValidationError'))
    ) {
      setCta({
        label: 'Check Availability',
        analyticsRef: 'check_availability',
      });
    } else if (
      checkInDate &&
      checkOutDate &&
      listing.supportsBookings &&
      quoteData &&
      quoteData.bookingQuoteByListingAndDates.__typename == 'BookingQuote'
    ) {
      setCta({
        label: 'Book now',
        subtext: `Booking with`,
        analyticsRef: 'select_listing_book_now',
        bookingUrl,
      });
    } else if (
      (!listing.supportsQuotes || !listing.supportsBookings) &&
      bookingUrl
    ) {
      setCta({
        label: 'Continue to Book',
        subtext: `Book on`,
        analyticsRef: 'select_listing_book_direct',
        bookingUrl,
      });
    } else {
      setCta({
        label: 'Book now',
        subtext: `Book on`,
        analyticsRef: 'select_listing_book_direct',
        bookingUrl,
      });
    }
  }, [
    quotesModalActive,
    quoteData,
    checkInDate,
    checkOutDate,
    adultCount,
    childCount,
    infantCount,
    petCount,
  ]);

  const [totalPriceInfo, setTotalPriceInfo] = React.useState<string>('');
  const [nightlyPriceInfo, setNightlyPriceInfo] = React.useState<string>('');

  React.useEffect(() => {
    if (shouldShowRatesAndAvailability && !datesPresent) {
      setNightlyPriceInfo('Add dates for prices');
      setTotalPriceInfo('Add dates for prices');
    } else if (
      shouldShowRatesAndAvailability &&
      datesPresent &&
      quoteData &&
      quoteData.bookingQuoteByListingAndDates.__typename == 'BookingQuote' &&
      quoteData.bookingQuoteByListingAndDates.totalCostCents
    ) {
      setNightlyPriceInfo(
        `${formatWholeDollarAmount(
          toInteger(
            (quoteData.bookingQuoteByListingAndDates.quoteItems.find(
              (item) => item.type === QuoteItemizedType.Base
            )?.costCents ||
              quoteData.bookingQuoteByListingAndDates.totalCostCents) /
              numberOfNights
          )
        )}`
      );
      setTotalPriceInfo(
        quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents
          ? formatWholeDollarAmount(
              toInteger(
                quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents
              )
            )
          : formatWholeDollarAmount(
              toInteger(quoteData.bookingQuoteByListingAndDates.totalCostCents)
            )
      );
    } else if (!quoteDataLoading || !shouldShowRatesAndAvailability) {
      setNightlyPriceInfo(priceRangeText());
      setTotalPriceInfo(priceRangeText());
    } else {
      setNightlyPriceInfo('');
      setTotalPriceInfo('');
    }
  }, [
    shouldShowRatesAndAvailability,
    datesPresent,
    quoteData,
    quoteDataLoading,
  ]);

  React.useEffect(() => {}, []);
  return (
    <div className="mx-auto">
      <ShareModal
        isOpen={shareModalActive}
        closeModal={deactivateShareModal}
        listing={listing}
      />

      <QuotesModal
        isOpen={quotesModalActive}
        closeModal={deactivateQuotesModal}
        listing={listing}
      />

      {listing.reviewsAverageRating && listing.reviewsCount ? (
        <ReviewsModal
          listingId={listing.id}
          reviewsCount={listing.reviewsCount}
          reviewsAverageRating={listing.reviewsAverageRating}
        />
      ) : null}

      {/* top-[65px] */}

      {/* Mobile CTA Bottom Banner */}
      <div
        className={cx(
          'block bg-white fixed w-full z-50 bottom-0 p-2 border-t -mx-4 sm:px-8 md:hidden pt-4 border border-gray-light shadow-2xl'
        )}
        style={{ zIndex: 1001 }}
      >
        <div className="flex flex-row items-center justify-between space-x-2 px-4">
          <div className="w-full justify-between text-gray-dark">
            <div className="flex h-8 w-full flex-col items-start justify-center">
              {totalPriceInfo !== '' ? (
                <div className="flex items-end space-x-1">
                  <Typography
                    variant="body2"
                    variantL="body1"
                    className="flex flex-col items-start whitespace-nowrap font-semibold text-gray-dark"
                  >
                    {totalPriceInfo}
                  </Typography>

                  <Typography
                    variant="body1"
                    className="mb-[1px] flex flex-col text-xs"
                  >
                    {datesPresent
                      ? bookingQuotePresent
                        ? ' before taxes'
                        : ' per night'
                      : null}
                  </Typography>
                </div>
              ) : (
                !quoteDataLoading && <Spinner />
              )}
            </div>

            {shouldShowRatesAndAvailability && (
              <div className="flex flex-col items-start underline pb-1.5">
                <Button
                  onClick={() => {
                    activateQuotesModal();
                  }}
                  size={'sm'}
                  variant="text"
                  data-id={'add_dates_button'}
                  className="p-0 focus:outline-none "
                >
                  {checkInDate && checkOutDate
                    ? `${formatDateMMMD(
                        moment(checkInDate)
                      )} - ${formatDateMMMD(moment(checkOutDate))}`
                    : 'Add Dates'}
                </Button>
              </div>
            )}
          </div>

          <div className="flex w-3/4 flex-col justify-center text-gray-dark">
            <div className="text-center">
              {quotesModalActive ||
              (shouldShowRatesAndAvailability && !quoteData) ||
              (shouldShowRatesAndAvailability &&
                !(checkInDate && checkOutDate)) ? (
                <Button
                  className="whitespace-nowrap"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (checkInDate && checkOutDate && quotesModalActive) {
                      deactivateQuotesModal();
                    } else {
                      activateQuotesModal();
                    }
                  }}
                  disabled={quotesModalActive && !(checkInDate && checkOutDate)}
                  data-id={cta.analyticsRef}
                  size="sm"
                  isFullWidth={true}
                >
                  {cta.label}
                </Button>
              ) : (
                cta.bookingUrl && (
                  <AnalyticsRef name={cta.analyticsRef}>
                    <Link
                      isButton={true}
                      isFullWidth={true}
                      className="whitespace-nowrap"
                      size="sm"
                      to={cta.bookingUrl}
                      target={listing.supportsBookings ? '' : '_blank'}
                      rel="noreferrer"
                      color="primary"
                    >
                      {cta.label}
                    </Link>
                  </AnalyticsRef>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        {/* Desktop Heading and title bar */}
        <div className="-mx-4 hidden items-center sm:mx-0 sm:flex sm:justify-between sm:pb-2 lg:items-end">
          <div className="overflow-hidden">
            <HeadingAndTitleBar listing={listing} />
          </div>
          <div className="hidden justify-end space-x-2 pb-2 pl-2 text-primary-main sm:flex sm:w-28">
            <button
              className="hidden w-full rounded-lg hover:bg-gray-xlight focus:outline-none sm:block"
              disabled={favLoading}
              onClick={() => handleFav(listing.id)}
            >
              <Icon
                name={IconNames.HEART}
                size={'lg'}
                solid={fav ? true : false}
                className="text-primary-main"
              />
            </button>

            <button
              className="hidden w-full rounded-lg hover:bg-gray-xlight focus:outline-none sm:block"
              style={{ outline: 'none' }}
              onClick={activateShareModal}
            >
              <Icon
                name={IconNames.SHARE}
                className="h-8 w-8 text-primary-main"
              />
            </button>
          </div>
        </div>

        {/* Image Mural */}
        <div className="overflow-none w-full sm:mx-0 sm:mt-2 sm:rounded-xl">
          <div className="absolute right-4 z-20 mt-4 rounded-lg sm:hidden">
            <FavoriteButton
              listingID={listing.id}
              listingIsFavorited={fav}
              size="md"
              onFavoritedSuccess={() => {
                setFav(!fav);
              }}
            />
          </div>

          <ImageMural
            altTag={`${listing.city!} Vacation Rental`}
            galleryAssets={listing.galleryAssets}
            shouldShowAllImages={true}
            shouldWatermarkImage={false}
          />
        </div>

        <div className="mx-auto pt-0 sm:pt-6 text-primary-main sm:pb-4">
          <div className="gap-8 space-y-8 md:grid md:grid-cols-24 md:space-y-0">
            <div className="space-y-6 rounded-lg bg-white px-2 py-4 sm:space-y-8 md:col-span-13 lg:col-span-16">
              <>
                {/* MOBILE HEADER */}
                <div className="block border-b border-off-white pb-4 sm:hidden sm:border-none ">
                  <HeadingAndTitleBar listing={listing} />
                </div>

                {/* BIO */}
                {(listing.account.companyName || listing.hostDisplayName) && (
                  <div className="pb-0 sm:px-2 sm:pb-6">
                    <ManagementBio
                      listingType={
                        listing.uniquePropertyType
                          ? enumToStringUniquePropertyTypes(
                              listing.uniquePropertyType
                            )
                          : listing.propertyType
                            ? enumToStringPropertyTypes(listing.propertyType)
                            : undefined
                      }
                      listingDetails={{
                        bedrooms: listing.numberOfRooms,
                        baths: listing.numberOfBathrooms,
                        capacity: listing.guestCapacity,
                      }}
                      account={{
                        id: listing.account.id,
                        slug: listing.account.slug,
                        avatarUrl:
                          listing.account.avatarUrl || listing.hostImageUrl,
                        companyName:
                          listing.account.companyName ||
                          listing.hostDisplayName,
                        marketingWebsiteUrl:
                          listing.account.marketingWebsiteUrl,
                      }}
                    />
                    {/* Ratings */}
                    {listing.reviewsAverageRating ? (
                      <div className="flex flex-row items-center space-x-2 pt-3 font-medium text-primary-dark">
                        <Icon
                          name={IconNames.STAR}
                          solid={true}
                          size="sm"
                          className="-mt-[0.05rem]"
                        />

                        <Typography variantM="subtitle2" component="span">
                          {Number.isInteger(listing.reviewsAverageRating)
                            ? listing.reviewsAverageRating?.toFixed(1)
                            : listing.reviewsAverageRating?.toFixed(2)}
                        </Typography>

                        <div className="px-1">&bull;</div>

                        {listing.reviewsCount && (
                          <div>
                            <Typography
                              variantM="subtitle2"
                              component="span"
                              className={
                                listing.reviews && listing.reviews.length > 0
                                  ? 'underline cursor-pointer'
                                  : ''
                              }
                              onClick={
                                listing.reviews && listing.reviews.length > 0
                                  ? activateReviewsModal
                                  : undefined
                              }
                            >
                              {listing.reviewsCount > 1
                                ? `${listing.reviewsCount} reviews`
                                : '1 review'}
                            </Typography>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Incentives */}
                <div className="space-y-0 border-b border-off-white pb-6 sm:pb-8 sm:px-2">
                  <div className="block sm:hidden pb-6">
                    {shouldShowRatesAndAvailability && datesPresent && (
                      <QuotesCompare />
                    )}
                  </div>
                  <div className="space-y-2 sm:space-y-8">
                    <div className="flex flex-row items-start space-x-4 sm:space-x-6">
                      <Image
                        src={noServiceFees}
                        alt="No service fees"
                        className="w-[40px] h-[40px] sm:w-[70px] sm:h-[70px]"
                        width={80}
                        height={80}
                      />

                      <div>
                        <Typography
                          component="span"
                          variant="body2"
                          variantM="body1"
                          className="font-semibold text-primary-dark md:text-2xl"
                        >
                          No Savvy service fees!
                        </Typography>
                        <div className="mt-[0.5px] leading-none sm:mt-1">
                          <Typography
                            component="span"
                            variant="small"
                            variantM="body2"
                            className="text-primary-main"
                          >
                            Save on your stay with no service or booking fees
                            from Savvy.
                          </Typography>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-row items-start space-x-4 sm:space-x-6">
                      <Image
                        src={professionalHosts}
                        alt="Professional hosts"
                        className="w-[40px] h-[40px] sm:w-[70px] sm:h-[70px]"
                        width={80}
                        height={80}
                      />

                      <div>
                        <Typography
                          component="span"
                          variant="body2"
                          variantM="body1"
                          className="font-semibold text-primary-main md:text-2xl"
                        >
                          Professional host
                        </Typography>
                        <div className="mt-[0.5px] leading-none sm:mt-1">
                          <Typography
                            component="span"
                            variant="small"
                            variantM="body2"
                            className="text-primary-main"
                          >
                            Enjoy peace of mind knowing your host is a
                            professional property manager that will ensure a
                            consistent and reliable stay.
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  {listing.description && (
                    <div className="border-b border-off-white pb-6 sm:px-2">
                      <Description description={listing.description} />
                    </div>
                  )}
                </div>

                {/* Feature List */}
                {featuresList.length > 0 && (
                  <div className="col-span-2 border-b border-off-white pb-6 sm:px-2">
                    <Typography
                      component="h4"
                      variant="subtitle2"
                      variantM="title4"
                      className="mb-3 sm:mb-6"
                    >
                      Amenities
                    </Typography>
                    <FeaturesSection
                      features={featuresList}
                      showAllEnabled={showAllAmenities}
                      toggleShowAll={toggleShowAllAmenities}
                    />
                  </div>
                )}

                {/* PM BIO */}
                {listing.account.companyName && listing.account.description && (
                  <div className="border-b border-off-white pb-2 sm:pb-6 sm:px-2">
                    <PropertyManagerBio
                      publicPhoneNumber={listing.account.phoneNumber}
                      publicEmail={listing.account.email}
                      account={listing.account}
                    />
                    <div className="mt-4 flex items-center justify-center space-x-4 lg:hidden">
                      {listing.account.marketingWebsiteUrl &&
                        !listing.marketingWebsiteUrl &&
                        !shouldShowWebsiteUrlAsButton && (
                          <Link
                            size={'sm'}
                            isButton={true}
                            to={appendUrlParams(
                              listing.account.marketingWebsiteUrl,
                              { utm_source: 'Savvy', utm_medium: 'website' }
                            )}
                            target="_blank"
                            rel="noreferrer"
                            color="primary"
                            variant="outlined"
                          >
                            Visit Website
                          </Link>
                        )}
                    </div>
                  </div>
                )}

                {/* Bio is PM bio is not present */}
                {listing.hostBio &&
                  !(
                    listing.account.companyName && listing.account.description
                  ) && (
                    <div className="border-b border-off-white pb-6 sm:px-2">
                      <InnkeeperBio
                        accountSlug={listing.account.slug || ''}
                        bio={listing.hostBio}
                        displayName={listing.hostDisplayName}
                        imageUrl={listing.hostImageUrl}
                      />
                    </div>
                  )}
                {/* Ratings and Reviews */}
                {listing.reviews && listing.reviews.length > 0 && (
                  <div className="col-span-2 pb-6 sm:px-2">
                    {listing.reviewsAverageRating ? (
                      <div className="mb-3 flex flex-row items-center space-x-1 text-sm font-semibold text-primary-dark sm:mb-6">
                        <Icon
                          name={IconNames.STAR}
                          solid={true}
                          className="-mt-0.5 text-sm md:text-lg"
                        />
                        <div>
                          <Typography
                            component="h4"
                            variant="subtitle2"
                            variantM="title4"
                          >
                            {Number.isInteger(listing.reviewsAverageRating)
                              ? listing.reviewsAverageRating?.toFixed(1)
                              : listing.reviewsAverageRating?.toFixed(2)}
                          </Typography>
                        </div>

                        <div className="px-1">&bull;</div>

                        {listing.reviewsCount && (
                          <Typography
                            component="h4"
                            variant="subtitle2"
                            variantM="title4"
                          >
                            {listing.reviewsCount > 1
                              ? `${listing.reviewsCount} reviews`
                              : '1 review'}
                          </Typography>
                        )}
                      </div>
                    ) : (
                      <Typography
                        component="h4"
                        variant="subtitle2"
                        variantM="title4"
                        className="mb-3 sm:mb-6"
                      >
                        Reviews
                      </Typography>
                    )}

                    {listing.reviews && listing.reviews.length > 0 && (
                      <ReviewsSection
                        reviews={listing.reviews}
                        showAllEnabled={showAllReviews}
                        toggleShowAll={toggleShowAllReviews}
                      />
                    )}
                  </div>
                )}
              </>
            </div>

            <div
              className="hidden md:col-span-11 md:block lg:col-span-8"
              style={{ zIndex: 1 }}
            >
              <div className="md:sticky md:top-6">
                <div className="bg-white shadow-xl md:rounded-lg md:border-2 md:border-primary-main">
                  <div className="px-6 sm:px-4">
                    {!shouldShowRatesAndAvailability ? (
                      <div className="pb-2">
                        {nightlyPriceInfo ? (
                          <div className="ml-1 space-x-[7px] pt-4 text-primary-main">
                            <Typography
                              component="span"
                              variant="body2"
                              variantL="body1"
                              className="font-semibold lg:font-semibold"
                            >
                              {nightlyPriceInfo}
                            </Typography>
                          </div>
                        ) : null}

                        {cta.bookingUrl && (
                          <div className="pt-4">
                            <AnalyticsRef name={cta.analyticsRef}>
                              <Link
                                isButton={true}
                                isFullWidth={true}
                                size="md"
                                to={cta.bookingUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="w-full flex justify-center md:max-w-md h-12 whitespace-nowrap"
                              >
                                {cta.label}
                              </Link>
                            </AnalyticsRef>
                            {cta.subtext && (
                              <div className="flex-cols-2 flex w-full flex-row justify-center space-x-1 whitespace-nowrap pt-2 text-xs">
                                <div className="font-light text-secondary-dark">
                                  {cta.subtext}
                                </div>
                                <div className="font-medium text-secondary-dark">
                                  {listing.account.companyName}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      // QUOTES ONLY
                      <div className="pt-4">
                        <QuotesCta
                          listing={listing}
                          nightlyPriceInfo={nightlyPriceInfo}
                          cta={cta}
                          shouldShowRatesAndAvailability={
                            shouldShowRatesAndAvailability
                          }
                        />
                      </div>
                    )}

                    <div className="space-y-8 px-2 pb-5">
                      {/* Sidebar details MD Visible */}

                      {!shouldShowBookingUrl &&
                        !shouldShowWebsiteUrlAsButton && (
                          <div>
                            <Typography
                              component="span"
                              variant="body2"
                              variantM="body1"
                              className="font-semibold md:font-semibold"
                            >
                              Contact
                            </Typography>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lower Part of Screen below Booking Bar */}
          <div
            id="map"
            className="scroll-mt-20 gap-8 space-y-6 divide-y divide-off-white pb-2  sm:pb-0 sm:py-6 md:grid md:grid-cols-6 md:space-y-0 px-2 sm:px-4"
          >
            <>
              {listing.lat && listing.lng && (
                <div className="col-span-6 pb-4">
                  <MapPreview
                    lat={listing.lat}
                    lng={listing.lng}
                    locationObfuscated={listing.locationObfuscated}
                    addressLineOne={listing.addressLineOne}
                    city={listing.city}
                    postalCode={listing.postalCode}
                    stateOrProvinceLongName={listing.stateOrProvinceLongName}
                  />
                </div>
              )}
            </>
          </div>

          <div className="gap-8 space-y-6 divide-y divide-off-white border-t border-off-white py-6 md:grid md:grid-cols-6 md:space-y-0 px-2 sm:px-4">
            {safetyFeatures.length > 0 && (
              <div className="col-span-2">
                <div>
                  <Typography
                    component="h4"
                    variantM="title4"
                    variant="subtitle2"
                    className="pb-3 sm:pb-6"
                  >
                    Safety & Inspections
                  </Typography>
                  <SafetyFeaturesSection features={safetyFeatures} />
                </div>
                {listingInspections.length > 0 && (
                  <div className={cx(safetyFeatures.length > 0 ? 'pt-3' : '')}>
                    <InspectionsSection affiliations={listingInspections} />
                  </div>
                )}
              </div>
            )}

            {listingAwards.length > 0 && (
              <div className="col-span-2 pt-4">
                <Typography
                  component="h4"
                  variantM="title4"
                  variant="subtitle2"
                  className="pb-3 sm:pb-6"
                >
                  Awards
                </Typography>
                <AwardsSection affiliations={listingAwards} />
              </div>
            )}

            {listingAssociations.length > 0 && (
              <div className="col-span-2 pt-4">
                <Typography
                  component="h4"
                  variantM="title4"
                  variant="subtitle2"
                  className="pb-3 sm:pb-6"
                >
                  Associations
                </Typography>
                <AssociationsSection affiliations={listingAssociations} />
              </div>
            )}

            {(listing.checkIn || listing.checkout || listing.licenseNumber) && (
              <div className="col-span-6 pt-4">
                <Typography
                  component="h4"
                  variantM="title4"
                  variant="subtitle2"
                  className="pb-3 sm:pb-6"
                >
                  Additional Details
                </Typography>
                <div className="flex flex-col space-y-2">
                  {listing.checkIn && (
                    <Typography
                      variant="body2"
                      variantM="body1"
                      component="span"
                    >{`Check In Time: ${enumSupportedCheckInAndOutTimesToString(
                      listing.checkIn
                    )}`}</Typography>
                  )}
                  {listing.checkout && (
                    <Typography
                      variant="body2"
                      variantM="body1"
                      component="span"
                    >{`Checkout Time: ${enumSupportedCheckInAndOutTimesToString(
                      listing.checkout
                    )}`}</Typography>
                  )}
                  {listing.licenseNumber && (
                    <Typography
                      variant="body2"
                      variantM="body1"
                      component="span"
                    >{`Business License Number: ${listing.licenseNumber}`}</Typography>
                  )}
                </div>
              </div>
            )}

            {listing.cancellationPolicy && (
              <div className="col-span-6 pt-6">
                <Typography
                  component="h4"
                  variantM="title4"
                  variant="subtitle2"
                >
                  Cancellation Policy
                </Typography>

                <div className="py-4">
                  <CancellationPolicy
                    cancellationPolicy={listing.cancellationPolicy}
                  />
                </div>
                <Typography
                  component="p"
                  variant="body2"
                  className="mt-8 italic text-gray-main"
                >
                  Please note that cancellation policies and terms are not set
                  by Savvy and are subject to change at the discretion of the
                  property owner.
                  <br></br>
                  <br></br>
                  To ensure that you understand your cancellation options, we
                  strongly recommend reviewing the property&#39;s cancellation
                  policy prior to booking your stay. For more information
                  regarding this property&#39;s cancellation policies and terms,
                  please visit the property&#39;s website or contact the
                  property directly.
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
